/**
 * CTA defaults — single source of truth for prefab cards and resolver logic.
 *
 * Used by:
 *  - Component render() functions (Hero, CTA, Navigation) for runtime fallback
 *  - Business settings UI for the card picker
 *  - Onboarding wizard for the same picker
 *
 * Backend has a mirror file at apps/api/src/modules/site-generation/cta-defaults.ts
 * (DEFAULT_CTA_LABEL + resolveCtaLabel/resolveCtaOfferLine signatures match).
 * Two copies of a small constant is fine; sharing a package across API + components
 * isn't worth the build complexity for this.
 */

export const DEFAULT_CTA_LABEL = "Call Now";
export const DEFAULT_CTA_OFFER_LINE: string | null = null;

export interface CtaCard {
  /** Telemetry seed — recorded as Business.ctaPresetSeed when the card is clicked. */
  seed: "call_now" | "schedule" | "free_quote";
  /** Card title shown to the owner. */
  cardLabel: string;
  /** One-line helper text on the card. */
  description: string;
  /** Value written to Business.ctaPrimaryLabel. */
  label: string;
  /** Value written to Business.ctaOfferLine. Null means "leave blank". */
  offerLine: string | null;
  /**
   * Which action this card puts first. Read as data rather than sniffed from
   * the label, so a card named "Talk to us" is not silently treated as a form.
   */
  posture: "phone" | "form";
}

export const CTA_CARDS: CtaCard[] = [
  {
    seed: "call_now",
    cardLabel: "Call Now",
    description: "Phone-first. No promotional claim.",
    label: "Call Now",
    offerLine: null,
    posture: "phone",
  },
  {
    seed: "schedule",
    cardLabel: "Schedule an Estimate",
    description: "Form-first. No promotional claim.",
    label: "Schedule an Estimate",
    offerLine: "By Appointment",
    posture: "form",
  },
  {
    seed: "free_quote",
    cardLabel: "Free Quote",
    description: "Form-first. Promotes free estimates.",
    label: "Get a Free Quote",
    offerLine: "Free Estimates",
    posture: "form",
  },
];

interface BusinessCtaShape {
  ctaPrimaryLabel?: string | null;
  ctaOfferLine?: string | null;
}

/** Resolve the effective CTA label: owner-set value, or the system default. */
export function resolveCtaLabel(business: BusinessCtaShape | undefined): string {
  const v = business?.ctaPrimaryLabel?.trim();
  return v && v.length > 0 ? v : DEFAULT_CTA_LABEL;
}

/** Resolve the effective offer line. Null means "no offer chip should render". */
export function resolveCtaOfferLine(business: BusinessCtaShape | undefined): string | null {
  const v = business?.ctaOfferLine?.trim();
  return v && v.length > 0 ? v : DEFAULT_CTA_OFFER_LINE;
}

/**
 * Should the primary CTA URL default to a phone link?
 * True when the label starts with "Call" (case-insensitive). Components apply this
 * only when the owner hasn't set an explicit ctaUrl prop.
 */
/** Neutral secondary label when the primary already calls. Deliberately not a
 *  borrowed offer ("Get a Free Quote") — the owner never agreed to one. */
export const SECONDARY_FORM_CTA_TEXT = "Contact Us";

/**
 * Where a contact button goes. The in-page anchor "#contact" is a dead click
 * on any page without an inline contact section, and legacy generated data
 * still carries it; the contact PAGE exists on every site, so buttons go
 * there. Stored values other than the legacy anchor are respected.
 */
export function contactUrl(url?: unknown): string {
  if (typeof url === "string" && url.trim() && url.trim() !== "#contact") {
    return url;
  }
  return "/contact";
}

export function resolvesToPhoneUrl(label: unknown): boolean {
  // In the Puck editor, fields with `contentEditable: true` are wrapped in an
  // <InlineTextField> React element before reaching the component's render() —
  // so callers (Hero/CTA/Navigation) may pass us a non-string value. Treat it
  // as "no phone bias"; the rendered text still works, the link just defaults
  // to "#contact" in editor preview when we can't inspect the underlying string.
  if (typeof label !== "string") return false;
  return /^call\b/i.test(label.trim());
}

/**
 * Which action the primary CTA performs.
 *
 * The card the owner picked in Business settings is the answer when there is
 * one — `ctaPresetSeed` records it. The label heuristic is only a fallback, for
 * businesses that predate the picker or typed their own label; it reads
 * "Call ..." as a phone CTA and everything else as a form.
 *
 * Callers use this to make the two hero buttons offer two different actions,
 * rather than a "Call Now" beside a "Call Us: (555) 123-4567".
 */
export function ctaPosture(
  business: { ctaPresetSeed?: string | null } | undefined,
  resolvedLabel: unknown,
): "phone" | "form" {
  const card = CTA_CARDS.find((c) => c.seed === business?.ctaPresetSeed);
  if (card) {
    return card.posture;
  }
  return resolvesToPhoneUrl(resolvedLabel) ? "phone" : "form";
}

